/** Two-character newsletter code: series letter + sequence (e.g. `N0`, `WQ`, `R0`). */
export type NewsletterProjectCode = `${string}${string}`;

/** Main series — issues #1–#36. Format mirrors work codes but letter comes first. */
export const NEWSLETTER_SERIES_MAIN = "N";

/** Overflow series — issues #37–#72. Additional letters extend capacity (see below). */
export const NEWSLETTER_SERIES_OVERFLOW = "W";

/** Recap / non-numbered specials (e.g. Director's Commentary). */
export const NEWSLETTER_SERIES_RECAP = "R";

const SEQUENCE = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * Numbered-issue series letters (first character of the code).
 * Avoid D/M/C/A/X — those are work portfolio category suffixes.
 * R is reserved for recap specials.
 *
 * Each series holds 36 issues → 20 series ≈ 720 numbered issues before expansion.
 */
export const NEWSLETTER_NUMBERED_SERIES = [
  "N",
  "W",
  "T",
  "B",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "O",
  "P",
  "Q",
  "S",
  "U",
  "V",
  "Y",
  "Z",
] as const;

const NEWSLETTER_SERIES_LETTERS = new Set<string>([
  ...NEWSLETTER_NUMBERED_SERIES,
  NEWSLETTER_SERIES_RECAP,
]);

export type NewsletterCodeSource = {
  slug: string;
  issueNumber: number | null;
  sendTime: string;
};

export function newsletterProjectCodePattern(code: string): code is NewsletterProjectCode {
  if (!/^[A-Z][0-9A-Z]$/.test(code)) {
    return false;
  }

  return NEWSLETTER_SERIES_LETTERS.has(code[0]!);
}

export function normalizeNewsletterProjectCode(
  value: string,
): NewsletterProjectCode | null {
  const code = value.trim().toUpperCase();
  if (!newsletterProjectCodePattern(code)) {
    return null;
  }
  return code;
}

/** Assign a stable code from the published issue number (not Mailchimp list order). */
export function assignNewsletterProjectCode(
  issueNumber: number | null,
): NewsletterProjectCode {
  if (issueNumber === null) {
    return `${NEWSLETTER_SERIES_RECAP}0`;
  }

  const index = issueNumber - 1;
  if (index < 0) {
    throw new Error(`Invalid newsletter issue number: ${issueNumber}`);
  }

  const seriesIndex = Math.floor(index / SEQUENCE.length);
  const sequenceIndex = index % SEQUENCE.length;

  if (seriesIndex >= NEWSLETTER_NUMBERED_SERIES.length) {
    throw new Error(
      `Too many numbered newsletter issues (${issueNumber}); add a series letter to NEWSLETTER_NUMBERED_SERIES.`,
    );
  }

  return `${NEWSLETTER_NUMBERED_SERIES[seriesIndex]!}${SEQUENCE[sequenceIndex]!}`;
}

export function buildNewsletterProjectCodeMaps(issues: NewsletterCodeSource[]): {
  codeBySlug: Record<string, NewsletterProjectCode>;
  slugByCode: Partial<Record<NewsletterProjectCode, string>>;
} {
  const codeBySlug: Record<string, NewsletterProjectCode> = {};
  const slugByCode: Partial<Record<NewsletterProjectCode, string>> = {};

  for (const issue of issues) {
    const code = assignNewsletterProjectCode(issue.issueNumber);
    if (slugByCode[code]) {
      throw new Error(
        `Duplicate newsletter project code "${code}" for slugs "${slugByCode[code]}" and "${issue.slug}".`,
      );
    }
    codeBySlug[issue.slug] = code;
    slugByCode[code] = issue.slug;
  }

  return { codeBySlug, slugByCode };
}

export function resolveNewsletterProjectCode(
  issue: NewsletterCodeSource,
): NewsletterProjectCode {
  return assignNewsletterProjectCode(issue.issueNumber);
}

export function getNewsletterSlugByProjectCode(
  code: NewsletterProjectCode,
  maps: { slugByCode: Partial<Record<NewsletterProjectCode, string>> },
): string | undefined {
  return maps.slugByCode[code];
}

export function isNewsletterProjectCode(
  value: string,
  maps: { slugByCode: Partial<Record<NewsletterProjectCode, string>> },
): boolean {
  const code = normalizeNewsletterProjectCode(value);
  return code != null && maps.slugByCode[code] != null;
}

/** Dev/CI guard — every indexed issue gets a unique two-character newsletter code. */
export function assertNewsletterProjectCodesComplete(
  issues: NewsletterCodeSource[],
): void {
  const slugs = new Set<string>();
  const codes = new Set<string>();

  for (const issue of issues) {
    if (slugs.has(issue.slug)) {
      throw new Error(`Duplicate newsletter slug in index: ${issue.slug}`);
    }
    slugs.add(issue.slug);

    const code = resolveNewsletterProjectCode(issue);
    if (!newsletterProjectCodePattern(code)) {
      throw new Error(`Invalid newsletter project code "${code}" for ${issue.slug}`);
    }
    if (codes.has(code)) {
      throw new Error(
        `Duplicate newsletter project code "${code}" for slug ${issue.slug}`,
      );
    }
    codes.add(code);
  }
}
